
import { useState, useMemo } from 'react';
import { allResources, libraryCategories, tradeFilters, formatFilters } from '../../../mocks/resourcesLibrary';

export default function LibraryGrid() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTrade, setActiveTrade] = useState('All Trades');
  const [activeFormat, setActiveFormat] = useState('All Formats');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular');
  const [showFilters, setShowFilters] = useState(false);

  const filteredResources = useMemo(() => {
    let results = [...allResources];

    if (activeCategory !== 'all') {
      results = results.filter((r) => r.category === activeCategory);
    }
    if (activeTrade !== 'All Trades') {
      results = results.filter((r) => r.trade === activeTrade);
    }
    if (activeFormat !== 'All Formats') {
      results = results.filter((r) => r.format === activeFormat);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      results = results.filter(
        (r) =>
          r.title.toLowerCase().includes(q) ||
          r.description.toLowerCase().includes(q) ||
          r.trade.toLowerCase().includes(q),
      );
    }

    if (sortBy === 'popular') {
      results.sort((a, b) => b.downloads - a.downloads);
    } else if (sortBy === 'newest') {
      results.sort((a, b) => (b.isNew ? 1 : 0) - (a.isNew ? 1 : 0));
    } else if (sortBy === 'az') {
      results.sort((a, b) => a.title.localeCompare(b.title));
    }

    return results;
  }, [activeCategory, activeTrade, activeFormat, searchQuery, sortBy]);

  const formatBadgeColor = (format: string) => {
    switch (format) {
      case 'PDF':
        return 'bg-red-100 text-red-700';
      case 'DOCX':
        return 'bg-sky-100 text-sky-700';
      case 'XLSX':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <section id="library" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full mb-6">
            <i className="ri-folder-download-line text-amber-600" />
            <span className="text-amber-700 text-sm font-medium">Download Centre</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Templates & <span className="text-amber-600">Checklists</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Ready-to-use business documents for every trade. Filter by category, trade, or format to find exactly what you need.
          </p>
        </div>

        {/* Search & Filter Bar */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search templates, checklists..."
                className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 transition-all"
              />
            </div>

            {/* Category Tabs */}
            <div className="flex items-center gap-2">
              {libraryCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeCategory === cat.id
                      ? 'bg-amber-500 text-white shadow-md shadow-amber-500/25'
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                  }`}
                >
                  <i className={cat.icon} />
                  <span className="hidden sm:inline">{cat.label}</span>
                  <span className={`text-xs px-1.5 py-0.5 rounded-md ${activeCategory === cat.id ? 'bg-white/20' : 'bg-slate-200'}`}>
                    {cat.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                showFilters ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
              }`}
            >
              <i className="ri-filter-3-line" />
              Filters
            </button>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-6">
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">Trade</label>
                <div className="flex flex-wrap gap-2">
                  {tradeFilters.map((trade) => (
                    <button
                      key={trade}
                      onClick={() => setActiveTrade(trade)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        activeTrade === trade
                          ? 'bg-teal-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {trade}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">Format</label>
                <div className="flex flex-wrap gap-2">
                  {formatFilters.map((fmt) => (
                    <button
                      key={fmt}
                      onClick={() => setActiveFormat(fmt)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                        activeFormat === fmt
                          ? 'bg-teal-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {fmt}
                    </button>
                  ))}
                </div>
              </div>
              <div className="ml-auto self-end">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2 block">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs text-slate-700 focus:outline-none focus:border-amber-400 cursor-pointer"
                >
                  <option value="popular">Most Downloaded</option>
                  <option value="newest">Newest First</option>
                  <option value="az">A – Z</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-slate-500">
            Showing <strong className="text-slate-900">{filteredResources.length}</strong> resources
          </p>
        </div>

        {/* Grid */}
        {filteredResources.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-search-line text-2xl text-slate-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No resources found</h3>
            <p className="text-slate-500 text-sm">Try adjusting your filters or search query.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource, index) => (
              <div
                key={resource.id}
                className="group bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm hover:shadow-xl hover:border-amber-200 transition-all duration-500"
                style={{ animationDelay: `${index * 60}ms` }}
              >
                {/* Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={resource.image}
                    alt={resource.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />

                  {/* Badges */}
                  <div className="absolute top-3 left-3 flex items-center gap-2">
                    <span className={`px-2.5 py-1 rounded-md text-xs font-semibold ${formatBadgeColor(resource.format)}`}>
                      {resource.format}
                    </span>
                    {resource.isNew && (
                      <span className="px-2.5 py-1 bg-teal-500 text-white rounded-md text-xs font-semibold">NEW</span>
                    )}
                    {resource.isPremium && (
                      <span className="px-2.5 py-1 bg-amber-500 text-white rounded-md text-xs font-semibold flex items-center gap-1">
                        <i className="ri-vip-crown-2-line text-[10px]" /> PRO
                      </span>
                    )}
                  </div>

                  {/* Category */}
                  <div className="absolute bottom-3 left-3">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-slate-700">
                      <i className={`${resource.icon} text-amber-500`} />
                      {resource.trade}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-semibold text-slate-900 leading-snug group-hover:text-amber-600 transition-colors line-clamp-2">
                      {resource.title}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-500 mb-4 line-clamp-2 leading-relaxed">
                    {resource.description}
                  </p>

                  {/* Checklist preview */}
                  {'items' in resource && resource.items && (
                    <div className="mb-4 flex flex-wrap gap-1.5">
                      {(resource.items as string[]).slice(0, 3).map((item) => (
                        <span key={item} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-50 rounded-md text-xs text-slate-600">
                          <i className="ri-check-line text-teal-500 text-[10px]" />
                          {item}
                        </span>
                      ))}
                      {(resource.items as string[]).length > 3 && (
                        <span className="px-2 py-1 bg-slate-50 rounded-md text-xs text-slate-400">
                          +{(resource.items as string[]).length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  {/* Meta */}
                  <div className="flex items-center justify-between text-xs text-slate-400 mb-4 pb-4 border-b border-slate-100">
                    <span className="flex items-center gap-1">
                      <i className="ri-file-line" />
                      {resource.fileSize}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-download-2-line" />
                      {resource.downloads.toLocaleString()} downloads
                    </span>
                  </div>

                  {/* Download Button */}
                  <button
                    className={`w-full py-3 rounded-xl font-medium flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap transition-all duration-300 ${
                      resource.isPremium
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 shadow-md shadow-amber-500/20'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    <i className={resource.isPremium ? 'ri-vip-crown-2-line' : 'ri-download-line'} />
                    {resource.isPremium ? 'Unlock with Pro' : 'Download Free'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
